import React, { useEffect, useState } from 'react';
import {
  PlusIcon, PencilSquareIcon, TrashIcon, MapPinIcon,
  AdjustmentsHorizontalIcon, ExclamationTriangleIcon,
  CheckBadgeIcon, PhoneIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { useStores } from '../../hooks/useStores';
import type { StorePayload } from '../../services/storeService';

type FormData = Partial<StorePayload> & { lat?: number; lng?: number };

const EMPTY_FORM: FormData = {
  name: '',
  phone_number: '',
  address_line1: '',
  suburb: '',
  city: '',
  radius_km: null,
  lat: undefined,
  lng: undefined,
  default_store: false,
  trading_hours: '',
};

function parseLocationPoint(location: string | null | undefined): { lat?: number; lng?: number } {
  if (!location) return {};
  if (location.startsWith('POINT')) {
    const match = location.match(/\(([^)]+)\)/);
    if (match) {
      const [lngStr, latStr] = match[1].split(' ');
      return { lat: Number(parseFloat(latStr).toFixed(6)), lng: Number(parseFloat(lngStr).toFixed(6)) };
    }
  } else if (/^[0-9A-F]+$/i.test(location)) {
    const buffer = new Uint8Array(location.match(/.{1,2}/g)!.map(b => parseInt(b, 16))).buffer;
    const view = new DataView(buffer);
    const le = view.getUint8(0) === 1;
    const lng = view.getFloat64(location.length > 50 ? 13 : 9, le);
    const lat = view.getFloat64(location.length > 50 ? 21 : 17, le);
    return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
  }
  return {};
}

function makeLocationPoint(lat?: number | null, lng?: number | null): string | null {
  if (lat == null || lng == null) return null;
  return `POINT(${lng} ${lat})`;
}

const FIELD = 'w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none';

export default function StoresPage() {
  const { stores, loading, fetchStores, createStore, updateStore, deleteStore } = useStores();

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [linkedCount, setLinkedCount] = useState(0);

  useEffect(() => { void fetchStores(); }, [fetchStores]);

  const openModal = (store?: typeof stores[0]) => {
    if (store) {
      const coords = parseLocationPoint((store).location);
      setFormData({
        name:          store.name,
        phone_number:  store.phone_number ?? '',
        address_line1: store.address_line1 ?? '',
        suburb:        store.suburb ?? '',
        city:          store.city ?? '',
        radius_km:     (store).radius_km ?? null,
        default_store: store.default_store ?? false,
        trading_hours: (store).trading_hours ?? '',
        lat:           coords.lat,
        lng:           coords.lng,
      });
      setEditingId(store.id);
    } else {
      setFormData(EMPTY_FORM);
      setEditingId(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setFormData(EMPTY_FORM); setEditingId(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: StorePayload = {
        name:          formData.name?.trim() ?? '',
        phone_number:  formData.phone_number?.trim() || null,
        address_line1: formData.address_line1?.trim() ?? '',
        suburb:        formData.suburb?.trim() || null,
        city:          formData.city?.trim() ?? '',
        radius_km:     formData.radius_km != null ? Number(formData.radius_km) : null,
        default_store: formData.default_store ?? false,
        trading_hours: formData.trading_hours?.trim() || null,
        location:      makeLocationPoint(formData.lat, formData.lng),
        latitude:      formData.lat ?? null,
        longitude:     formData.lng ?? null,
      };

      const result = editingId
          ? await updateStore(editingId, payload)
          : await createStore(payload);

      if (result) closeModal();
      else alert('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await deleteStore(id);
    if (!result.success && result.linkedCount) {
      setLinkedCount(result.linkedCount);
      setIsWarningOpen(true);
      return;
    }
    if (!result.success) return;
  };

  const field = (key: keyof FormData) => ({
    value: (formData[key] ?? '') as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setFormData(p => ({ ...p, [key]: e.target.value })),
  });

  const filteredItems = stores.filter(s => {
    const q = searchQuery.toLowerCase();
    return (
        s.name.toLowerCase().includes(q) ||
        s.address_line1?.toLowerCase().includes(q) ||
        s.city?.toLowerCase().includes(q) ||
        (s.suburb && s.suburb.toLowerCase().includes(q))
    );
  });

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-stone-900">Stores</h2>
            <p className="text-sm text-stone-500 mt-1">Manage store locations and delivery radius</p>
          </div>
          <button onClick={() => openModal()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] transition whitespace-nowrap shadow-sm">
            <PlusIcon className="h-4 w-4" />
            Add Store
          </button>
        </div>

        {/* Search */}
        <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by store name, address, or suburb…"
            className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]"
        />

        {/* Store list */}
        {loading ? (
            <div className="py-12 text-center text-stone-400 text-sm">Loading stores…</div>
        ) : filteredItems.length === 0 ? (
            <div className="rounded-2xl border border-stone-200 bg-white px-6 py-12 text-center shadow-sm">
              <p className="text-stone-400">{stores.length === 0 ? 'No stores found.' : 'No stores match your search.'}</p>
            </div>
        ) : (
            <div className="space-y-3">
              {filteredItems.map(store => (
                  <div key={store.id}
                       className={`rounded-xl border shadow-sm px-4 py-4 transition-colors ${
                           store.default_store
                               ? 'border-[#ee2b74] bg-[#ee2b74]/5'
                               : 'border-stone-200 bg-white hover:bg-stone-50/50'
                       }`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-stone-900 text-base">{store.name}</h3>
                          {store.default_store && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ee2b74] text-white text-[10px] font-bold uppercase tracking-wider">
                                                <CheckBadgeIcon className="h-3 w-3" /> Default
                                            </span>
                          )}
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-stone-600">
                          <div className="flex items-start gap-2">
                            <MapPinIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-400" />
                            <span>{[store.address_line1, store.suburb, store.city].filter(Boolean).join(', ')}</span>
                          </div>
                          {(store).trading_hours && (
                              <div className="flex items-start gap-2">
                                <ClockIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-400" />
                                <span>{(store).trading_hours}</span>
                              </div>
                          )}
                          <div className="flex flex-wrap gap-4 mt-1">
                            {store.phone_number && (
                                <div className="flex items-center gap-2 text-xs text-stone-500">
                                  <PhoneIcon className="h-3.5 w-3.5 text-stone-400" />
                                  {store.phone_number}
                                </div>
                            )}
                            {(store).radius_km != null && (
                                <div className="flex items-center gap-2 text-xs text-[#ee2b74] font-medium">
                                  <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
                                  Radius: {(store).radius_km} km
                                </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => openModal(store)}
                                className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 transition">
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleDelete(store.id)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition">
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
        )}

        {/* Edit / Create modal */}
        {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
                <div className="h-1 bg-[#ee2b74] w-full flex-shrink-0" />
                <div className="p-6 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-stone-900 mb-6">
                    {editingId ? 'Edit Store' : 'Add New Store'}
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Store Name *</label>
                      <input type="text" required className={FIELD} {...field('name')} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Phone Number</label>
                      <input type="tel" placeholder="+27..." className={FIELD} {...field('phone_number')} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Street Address *</label>
                      <input type="text" required className={FIELD} {...field('address_line1')} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-stone-700 mb-1.5">Suburb</label>
                        <input type="text" className={FIELD} {...field('suburb')} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-700 mb-1.5">City *</label>
                        <input type="text" required className={FIELD} {...field('city')} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-stone-700 mb-1.5">Latitude</label>
                        <input type="number" step="any" className={FIELD}
                               value={formData.lat ?? ''}
                               onChange={e => setFormData(p => ({ ...p, lat: e.target.value ? parseFloat(e.target.value) : undefined }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-stone-700 mb-1.5">Longitude</label>
                        <input type="number" step="any" className={FIELD}
                               value={formData.lng ?? ''}
                               onChange={e => setFormData(p => ({ ...p, lng: e.target.value ? parseFloat(e.target.value) : undefined }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Radius (km)</label>
                      <input type="number" step="any" className={FIELD}
                             value={formData.radius_km ?? ''}
                             onChange={e => setFormData(p => ({ ...p, radius_km: e.target.value ? parseFloat(e.target.value) : null }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Trading Hours</label>
                      <input type="text" placeholder="Weekdays/Weekends: 7am - 7pm" className={FIELD} {...field('trading_hours')} />
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl border border-stone-200 bg-stone-50 hover:border-[#ee2b74]/30 transition-colors">
                      <input
                          type="checkbox"
                          id="default_store"
                          checked={formData.default_store ?? false}
                          onChange={e => setFormData(p => ({ ...p, default_store: e.target.checked }))}
                          className="h-5 w-5 rounded border-stone-300 accent-[#ee2b74] cursor-pointer"
                      />
                      <label htmlFor="default_store" className="flex-1 text-sm font-medium text-stone-700 cursor-pointer">
                        Mark as Default Store
                        <p className="text-xs font-normal text-stone-500 mt-0.5">Setting this will unmark any other default store.</p>
                      </label>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                      <button type="button" onClick={closeModal}
                              className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50">
                        Cancel
                      </button>
                      <button type="submit" disabled={saving}
                              className="px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] disabled:opacity-60 transition">
                        {saving ? 'Saving…' : editingId ? 'Update Store' : 'Create Store'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}

        {/* Deletion blocked warning */}
        {isWarningOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-[70] bg-black/40 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
                <div className="h-1.5 bg-[#ee2b74] w-full" />
                <div className="p-6 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 mb-4">
                    <ExclamationTriangleIcon className="h-6 w-6 text-[#ee2b74]" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-900">Deletion Blocked</h3>
                  <p className="mt-2 text-sm text-stone-500 leading-relaxed">
                    This store is linked to <span className="font-semibold text-stone-700">{linkedCount} product{linkedCount !== 1 ? 's' : ''}</span>. Remove or reassign those products before deleting this store.
                  </p>
                  <button onClick={() => setIsWarningOpen(false)}
                          className="mt-6 w-full rounded-xl bg-[#ee2b74] px-4 py-3 text-sm font-bold text-white hover:bg-[#d4245f] transition">
                    Close
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}